(() => {
  const selectors = {
    slider: '[data-origin-video-slider]',
    wrap: '[data-origin-video-wrap]',
    video: 'video[data-origin-video]',
    playButton: '[data-origin-play-btn]',
  };

  class OriginVideoController {
    constructor(slider) {
      this.slider = slider;
      this.swiper = null;
      this.hasBoundSwiper = false;
      this.intersectionObserver = null;
    }

    init() {
      const wraps = this.slider.querySelectorAll(selectors.wrap);
      if (!wraps.length) return;

      wraps.forEach((wrap) => this.setupWrap(wrap));
      this.observeVisibility();
      this.bindSwiperWhenReady();
    }

    setupWrap(wrap) {
      const playButton = wrap.querySelector(selectors.playButton);

      const play = () => {
        const video = wrap.querySelector(selectors.video);
        if (!video) return;

        if (video.preload === 'none' || video.readyState === 0) {
          video.preload = 'auto';
          video.load();
          video.addEventListener('canplay', () => video.play().catch(() => {}), { once: true });
        } else {
          video.play().catch(() => {});
        }

        playButton?.classList.add('origin-video__play--hidden');
        wrap.classList.add('origin-video-wrap--playing');
      };

      const pause = () => {
        const video = wrap.querySelector(selectors.video);
        video?.pause();
        playButton?.classList.remove('origin-video__play--hidden');
        wrap.classList.remove('origin-video-wrap--playing');
      };

      wrap._originVideoPlay = play;
      wrap._originVideoPause = pause;

      playButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        play();
      });

      wrap.addEventListener('click', (event) => {
        if (event.target.closest(selectors.playButton)) return;

        const video = wrap.querySelector(selectors.video);
        if (video && !video.paused) pause();
      });

      wrap.querySelector(selectors.video)?.addEventListener('ended', pause);
    }

    pauseAll() {
      this.slider.querySelectorAll(selectors.wrap).forEach((wrap) => wrap._originVideoPause?.());
    }

    preloadActiveVideo() {
      const activeSlide = this.slider.querySelector('.swiper-slide-active') || this.slider.querySelector('.swiper-slide');
      const activeVideo = activeSlide?.querySelector(selectors.video);

      if (activeVideo && activeVideo.preload === 'none') {
        activeVideo.preload = 'auto';
      }
    }

    bindSwiperWhenReady() {
      if (this.slider.querySelectorAll('.swiper-slide').length <= 1) {
        this.preloadActiveVideo();
        return;
      }

      const bind = () => {
        const swiper = this.slider.elements?.swiperInstance || this.slider.swiperInstance;

        if (!swiper) {
          window.requestAnimationFrame(bind);
          return;
        }

        if (this.hasBoundSwiper) return;

        this.swiper = swiper;
        this.hasBoundSwiper = true;
        this.preloadActiveVideo();
        swiper.on?.('slideChange', () => {
          this.pauseAll();
          this.preloadActiveVideo();
        });
      };

      bind();
    }

    observeVisibility() {
      if (!window.IntersectionObserver) return;

      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) this.pauseAll();
        });
      }, { threshold: 0.5 });

      this.intersectionObserver.observe(this.slider);
    }
  }

  const initOriginVideos = (root = document) => {
    root.querySelectorAll(selectors.slider).forEach((slider) => {
      if (slider.dataset.originVideoInitialized === 'true') return;
      slider.dataset.originVideoInitialized = 'true';
      new OriginVideoController(slider).init();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initOriginVideos());
  } else {
    initOriginVideos();
  }

  document.addEventListener('shopify:section:load', (event) => initOriginVideos(event.target));
})();
